import { WidgetDefinition, WidgetCategory, WidgetProps } from './types';

// ============================================================================
// Widget Registry
// ============================================================================

const widgetRegistry = new Map<string, WidgetDefinition>();

/**
 * Register a widget definition in the registry
 */
export function registerWidget<TProps extends WidgetProps = WidgetProps>(
  definition: WidgetDefinition<TProps>
): void {
  if (widgetRegistry.has(definition.id)) {
    console.warn(`Widget "${definition.id}" is already registered. Overwriting.`);
  }
  widgetRegistry.set(definition.id, definition as WidgetDefinition);
}

/**
 * Get a widget definition by ID
 */
export function getWidget(id: string): WidgetDefinition | undefined {
  return widgetRegistry.get(id);
}

/**
 * Get all registered widgets
 */
export function getAllWidgets(): WidgetDefinition[] {
  return Array.from(widgetRegistry.values());
}

/**
 * Get widgets filtered by category
 */
export function getWidgetsByCategory(category: WidgetCategory): WidgetDefinition[] {
  return getAllWidgets().filter((widget) => widget.category === category);
}

/**
 * Check if a widget is registered
 */
export function isWidgetRegistered(id: string): boolean {
  return widgetRegistry.has(id);
}

/**
 * Get widget count
 */
export function getWidgetCount(): number {
  return widgetRegistry.size;
}

/**
 * Unregister a widget (useful for testing or dynamic loading)
 */
export function unregisterWidget(id: string): boolean {
  return widgetRegistry.delete(id);
}

/**
 * Clear all registered widgets (useful for testing)
 */
export function clearRegistry(): void {
  widgetRegistry.clear();
}

/**
 * Get all unique categories that have registered widgets
 */
export function getActiveCategories(): WidgetCategory[] {
  const categories = new Set<WidgetCategory>();
  for (const widget of widgetRegistry.values()) {
    categories.add(widget.category);
  }
  return Array.from(categories);
}

/**
 * Search widgets by name or description
 */
export function searchWidgets(query: string): WidgetDefinition[] {
  const lowercaseQuery = query.toLowerCase().trim();
  if (!lowercaseQuery) {
    return getAllWidgets();
  }
  return getAllWidgets().filter(
    (widget) =>
      widget.name.toLowerCase().includes(lowercaseQuery) ||
      widget.description.toLowerCase().includes(lowercaseQuery)
  );
}

/**
 * Get widgets that the user has permission to use
 */
export function getAccessibleWidgets(userPermissions: string[]): WidgetDefinition[] {
  return getAllWidgets().filter((widget) => {
    if (!widget.permissions || widget.permissions.length === 0) {
      return true;
    }
    return widget.permissions.some((perm) => userPermissions.includes(perm));
  });
}
